import _ from 'lodash';
import { publish, subscribe } from '@jutro/events';
import { AdobeAnalyticsEventService, SphAdobeAnalyticsUtil, ADOBE_TOPICS } from 'sg-adobe-analytics';
import SphConstants from 'sg-sph-constants';
import SphUtils from 'sg-sph-utils';
import { SgSubmissionUtil } from 'saga-modules';
import CommonAdobeAnalyticsAdapter from '../Common/CommonAdobeAnalyticsAdapter';
import { SG_AA_FIELDS_ADDONS, SG_AA_PRODUCT_NAME } from '../AdobeAnalyticsConstants';

let { tagging } = {};
let { taggingProperties } = {};

function populateYourPropertyDetailsAdobeTracking(propertyDetails) {
    const propertyDetailsTagging = [
        { adobeID: taggingProperties.propertyCategory, value: propertyDetails.propertyCategory },
        { adobeID: taggingProperties.approxYearBuild, value: propertyDetails.approxYearBuild },
        { adobeID: taggingProperties.numberOfBedrooms, value: { code: propertyDetails.numberOfBedrooms } },
        { adobeID: taggingProperties.moreThanQuarterRoofFlat, value: propertyDetails.moreThanQuarterRoofFlat },
        { adobeID: taggingProperties.moreThanHalfRoofFlat, value: propertyDetails.moreThanHalfRoofFlat },
        {
            adobeID: taggingProperties.propertyDetails,
            value: SphAdobeAnalyticsUtil.createPropertyDetailsData(propertyDetails)
        },
    ];
    if (propertyDetails.propertyType) {
        propertyDetailsTagging.push({ adobeID: taggingProperties.propertyType, value: { code: propertyDetails.propertyType } });
    }
    _.each(propertyDetailsTagging, (taggingValue) => {
        publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: taggingValue.adobeID, value: taggingValue.value });
    });
}
function populateProductDetails(property) {
    const productDetailsTagging = [
        { adobeID: taggingProperties.productName, value: SphAdobeAnalyticsUtil.determineProductName(property) }
    ];
    _.each(productDetailsTagging, (taggingValue) => {
        publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: taggingValue.adobeID, value: taggingValue.value });
    });
}
function populatePropertyDetailsAdobeTracking(property) {
    const propertyTagging = [
        { adobeID: taggingProperties.propertyCategory, value: property.propertyCategory },
        { adobeID: taggingProperties.approxYearBuild, value: property.approxYearBuild },
        { adobeID: taggingProperties.numberOfBedrooms, value: { code: property.numberOfBedrooms } },
        { adobeID: taggingProperties.moreThanQuarterRoofFlat, value: property.moreThanQuarterRoofFlat },
        { adobeID: taggingProperties.moreThanHalfRoofFlat, value: property.moreThanHalfRoofFlat },
        { adobeID: taggingProperties.isMainResidence, value: property.isMainResidence },
        { adobeID: taggingProperties.propertyRented, value: property.propertyRented === undefined ? false : property.propertyRented },
        { adobeID: taggingProperties.contentsSumInsured, value: property.contentsSumInsured },
        { adobeID: taggingProperties.contentsCoverValue, value: { code: property.contentsCoverValue } },
        { adobeID: taggingProperties.contentsCoverValue, value: { code: property.contentsValue } },
        { adobeID: taggingProperties.contentsValueTotal, value: property.contentsTotal.amount },
        { adobeID: taggingProperties.hasLegalCover, value: property.hasLegalCover },
        { adobeID: taggingProperties.hasSheCover, value: property.hasSheCover },
        { adobeID: taggingProperties.hasHeatingCover, value: property.hasHeatingCover },
        { adobeID: taggingProperties.hasBoilerCover, value: property.hasBoilerCover },
        { adobeID: taggingProperties.unspecifiedItemsCover, value: { code: property.unspecifiedItemsCover } },
        { adobeID: taggingProperties.hasBuildingsAccidentCover, value: property.hasBuildingsAccidentCover },
        { adobeID: taggingProperties.hasContentsAccidentCover, value: property.hasContentsAccidentCover },
        { adobeID: taggingProperties.hasFixedPrice, value: property.hasFixedPrice },
        {
            adobeID: taggingProperties.propertyDetails,
            value: SphAdobeAnalyticsUtil.createPropertyDetailsData(property)
        },
    ];
    if (property.propertyType) {
        propertyTagging.push({ adobeID: taggingProperties.propertyType, value: { code: property.propertyType } });
    }
    if (property.landlordContentsTotal) {
        propertyTagging.push({ adobeID: taggingProperties.landlordContentsTotal, value: { code: property.landlordContentsTotal } });
    }
    if (property.isMainResidence) {
        propertyTagging.push({ adobeID: taggingProperties.propertyUse, value: 'Main residence' });
    } else if (property.propertyUse) {
        propertyTagging.push({ adobeID: taggingProperties.propertyUse, value: { code: property.propertyUse } });
    }
    _.each(propertyTagging, (taggingValue) => {
        publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: taggingValue.adobeID, value: taggingValue.value });
    });
}

function populatePHDetailsAdobeTracking(submission) {
    const { coverables } = submission.lobData.privateHome;
    const policyHolderDetailsTagging = [
        { adobeID: taggingProperties.titlePolicyHolder, value: submission.baseData.homeAccountHolder.prefix },
        { adobeID: taggingProperties.dateOfBirth, value: submission.baseData.homeAccountHolder.dateOfBirth },
        { adobeID: taggingProperties.employmentStatusSelectPolicyHolder, value: submission.baseData.homeAccountHolder.employmentStatus },
        { adobeID: taggingProperties.occupationTypeSelectPolicyHolder, value: { code: submission.baseData.homeAccountHolder.occupation } },
        { adobeID: taggingProperties.hasJointPolicyHolder, value: submission.lobData.privateHome.coverables.hasJointPolicyHolder },
        { adobeID: taggingProperties.hasSubsidenceHeaveLandslip, value: coverables.morePropertyDetails.hasSubsidenceHeaveLandslip },
        { adobeID: taggingProperties.hasFloodingAtProperty, value: coverables.morePropertyDetails.hasFloodingAtProperty },
        { adobeID: taggingProperties.sourceOfBusiness, value: { code: submission.baseData.homeAccountHolder.sourceOfBusiness } },
        { adobeID: taggingProperties.promoCode, value: coverables.otherInfoAboutYou.promoCode },
        { adobeID: taggingProperties.claimsArray, value: coverables.otherInfoAboutYou.claims },
        { adobeID: taggingProperties.hasHomeClaims, value: coverables.otherInfoAboutYou.hasHomeClaims },
        { adobeID: taggingProperties.currentPaymentType, value: { code: coverables.otherInfoAboutYou.currentPaymentType } }
    ];
    _.each(policyHolderDetailsTagging, (taggingValue) => {
        publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: taggingValue.adobeID, value: taggingValue.value });
    });
}
function populateAddOnsArray(submission) {
    const productActionReason = '';
    const productActionLocation = '';
    const { propertyDetails } = submission.lobData.privateHome.coverables;
    const { coverages } = !_.isEmpty(submission.lobData.privateHome.offerings) && submission.lobData.privateHome.offerings[0];
    const { isSagaPlus } = coverages !== undefined && coverages;

    AdobeAnalyticsEventService.emitAddOnChangeEvent(
        {
            name: SG_AA_FIELDS_ADDONS.homeLegalExpensesCoverQuestion.name,
            amount: { amount: SG_AA_FIELDS_ADDONS.homeLegalExpensesCoverQuestion.price },
            selected: propertyDetails.hasLegalCover
        },
        productActionReason,
        productActionLocation
    );

    if (!isSagaPlus) {
        AdobeAnalyticsEventService.emitAddOnChangeEvent(
            {
                name: SG_AA_FIELDS_ADDONS.hasContentsAccidentCover.name,
                amount: { amount: undefined },
                selected: propertyDetails.hasContentsAccidentCover
            },
            productActionReason,
            productActionLocation
        );
        AdobeAnalyticsEventService.emitAddOnChangeEvent(
            {
                name: SG_AA_FIELDS_ADDONS.hasBuildingsAccidentCover.name,
                amount: { amount: undefined },
                selected: propertyDetails.hasBuildingsAccidentCover
            },
            productActionReason,
            productActionLocation
        );
    }

    AdobeAnalyticsEventService.emitAddOnChangeEvent(
        {
            name: SG_AA_FIELDS_ADDONS.unspecifiedItemsCover.name,
            amount: { amount: undefined },
            valueMap: SG_AA_FIELDS_ADDONS.unspecifiedItemsCover.valueMap[SphAdobeAnalyticsUtil.unspecifiedItemValueMap(submission)] || undefined,
            selected: propertyDetails.unspecifiedItemsCover !== undefined && propertyDetails.unspecifiedItemsCover !== SphConstants.NONE_PREFIX
        },
        productActionReason,
        productActionLocation
    );
    AdobeAnalyticsEventService.emitAddOnChangeEvent(
        {
            name: SG_AA_FIELDS_ADDONS.hasSheCover.name,
            amount: { amount: undefined },
            valueMap: SG_AA_FIELDS_ADDONS.hasSheCover.valueMap[SphAdobeAnalyticsUtil.sheValueMapOnResume(submission)] || undefined,
            selected: submission.lobData.privateHome.coverables.propertyDetails.hasSheCover
        },
        productActionReason,
        productActionLocation
    );
}

function populateQuoteAdobeTracking(submission, isAddOnChange) {
    const { coverages } = !_.isEmpty(submission.lobData.privateHome.offerings) && submission.lobData.privateHome.offerings[0];
    const { isSagaPlus } = coverages !== undefined && coverages;
    const productValue = isSagaPlus ? SG_AA_PRODUCT_NAME.HOME_FIXED : SG_AA_PRODUCT_NAME.HOME;
    const quoteTagging = [];
    if (!_.isEmpty(submission.lobData.privateHome.offerings)) {
        const buildingCoverLevel = SgSubmissionUtil.getTermByCode(
            SgSubmissionUtil.getCoverageByCode(SphUtils.basicCoverages(submission), SphConstants.COVERAGES_CODES.Building),
            SphConstants.COVERAGES_CODES.BuildingLevelTermName
        );
        const contentsCoverlevel = SgSubmissionUtil.getTermByCode(
            SgSubmissionUtil.getCoverageByCode(SphUtils.basicCoverages(submission), SphConstants.COVERAGES_CODES.Contents),
            SphConstants.COVERAGES_CODES.ContentsLevelTermName
        );
        quoteTagging.push(
            { adobeID: taggingProperties.buildingsCoverLevel, value: buildingCoverLevel.chosenTerm },
            { adobeID: taggingProperties.contentsCoverLevel, value: contentsCoverlevel.chosenTerm },
            { adobeID: taggingProperties.quoteNumber, value: submission.quoteID },
        );
    }
    quoteTagging.push(
        { adobeID: taggingProperties.productName, value: productValue },
        { adobeID: taggingProperties.product, value: SphAdobeAnalyticsUtil.determineProduct(isSagaPlus) }
    );
    _.each(quoteTagging, (taggingValue) => {
        publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: taggingValue.adobeID, value: taggingValue.value, isAddOnChange });
    });
    delete tagging.quoteSection;
}

function populateSpecItemAdobeTracking(submission) {
    const specItemTagging = [];
    if (!_.isEmpty(submission.lobData.privateHome.coverables.specifiedItems)) {
        specItemTagging.push(
            { adobeID: taggingProperties.specItemsArray, value: submission.lobData.privateHome.coverables.specifiedItems }
        );
    }

    _.each(specItemTagging, (taggingValue) => {
        publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: taggingValue.adobeID, value: taggingValue.value });
    });
}
function setAdobeTrackingData(submission) {
    const { coverages } = !_.isEmpty(submission.lobData.privateHome.offerings) && submission.lobData.privateHome.offerings[0];
    const { isSagaPlus } = coverages !== undefined && coverages;
    const generalTagging = [
        { adobeID: taggingProperties.coverLevelType, value: { code: submission.lobData.privateHome.coverables.coverLevel } },
        {
            adobeID: taggingProperties.productName,
            value: SphAdobeAnalyticsUtil.determineProductName(submission.lobData.privateHome.coverables.propertyDetails)
        },
        {
            adobeID: taggingProperties.product,
            value: SphAdobeAnalyticsUtil.determineProduct(isSagaPlus)
        },
        { adobeID: taggingProperties.periodStartDate, value: submission.baseData.periodStartDate },
        { adobeID: taggingProperties.postalCode, value: submission.baseData.policyAddress.postalCode }
    ];
    _.each(generalTagging, (taggingValue) => {
        publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: taggingValue.adobeID, value: taggingValue.value, model: taggingValue.model });
    });
    populatePropertyDetailsAdobeTracking(submission.lobData.privateHome.coverables.propertyDetails); // Property-specific
    populatePHDetailsAdobeTracking(submission); // Details-specific
    populateQuoteAdobeTracking(submission); // Quote specific
    populateSpecItemAdobeTracking(submission); // SpecItem specific
}

function cleanup() {
    CommonAdobeAnalyticsAdapter.cleanup();
}

export default class SphAdobeAnalyticsAdapter {
    static init(productTagging, adobeTagging) {
        tagging = adobeTagging;
        taggingProperties = productTagging;

        subscribe([ADOBE_TOPICS.RESUME_TRACKING], (event) => {
            cleanup();
            CommonAdobeAnalyticsAdapter.populateQuoteAdobeTracking(event.model);
            setAdobeTrackingData(event.model.value);
            populateAddOnsArray(event.model.value);
        });
        subscribe([ADOBE_TOPICS.PROPERTY_DETAILS_TRACKING], (event) => {
            populateYourPropertyDetailsAdobeTracking(event.details);
        });
        subscribe([ADOBE_TOPICS.SET_PRODUCT_DETAILS], (event) => {
            populateProductDetails(event.propertyVM.value);
        });
        subscribe([ADOBE_TOPICS.SUBMISSION_QUOTED], (event) => {
            CommonAdobeAnalyticsAdapter.populateQuoteAdobeTracking(event.model);
            populateQuoteAdobeTracking(event.model.value, event.isAddOnChange);
        });
    }
}
